/**
 * Document API Endpoints (DocMana Integration) — DEV-6164.
 *
 * All endpoints hit /api/v1/documents/* on the BFF. The wire carries the
 * typed DocumentTypeRef pair (category, subtype) — no legacy Docmana camelCase
 * strings remain on the FE→BFF boundary. The /api/v2/* surface has been
 * collapsed back into /api/v1/* now that DEV-6164 owns the v1 wire shape.
 *
 * All upload endpoints use query parameters for metadata and FormData for the
 * file payload. Responses are wrapped in SuccessResponse<T>; the helpers
 * unwrap to T.
 */

import axiosInstance from '../axiosInstance';
import { appendSafeFile } from '../../../utils/fileUtils';
import type {
  SuccessResponse,
  InvoiceUploadResponse,
  ProofUploadResponse,
  VoidCheckUploadResponse,
  RequiredDocumentsRequest,
  RequiredDocumentsResponse,
} from '../../../types/api';
import type { DocumentTypeRef } from '../../../utils/documentTypeRef';

/**
 * Upload invoice document for AI line item extraction.
 *
 * POST /api/v1/documents/uploadInvoice
 */
export async function uploadInvoice(
  file: File,
  eventNumber: string,
  claimType: string,
  documentKey: string,
  referenceNumber: string,
  clientMessage?: string
): Promise<InvoiceUploadResponse> {
  const formData = new FormData();
  appendSafeFile(formData, 'file', file);
  // clientMessage rides in the multipart body, not the query-string: free text with French
  // apostrophes/newlines trips the Azure WAF SQLI/CRLF rules when URL-encoded (DEV-7030).
  // Spring's @RequestParam resolves it from the form-field, so the BFF contract is unchanged.
  if (clientMessage) {
    formData.append('clientMessage', clientMessage);
  }

  const response = await axiosInstance.post<SuccessResponse<InvoiceUploadResponse>>(
    '/api/v1/documents/uploadInvoice',
    formData,
    {
      params: {
        eventNumber,
        claimType,
        documentKey,
        referenceNumber,
      },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 360000,
      suppressGlobalErrorToast: true,
    }
  );

  return response.data.payload;
}

/**
 * Upload proof document for AI classification.
 *
 * POST /api/v1/documents/uploadProof
 *
 * @param file — proof document (PDF, JPG, PNG, HEIC; max 10MB).
 * @param eventNumber — Event__c.Name (e.g. `"3220906"`), NOT the Salesforce id.
 * @param claimType — `"medical"` | `"cancellation"` | `"baggage"`.
 * @param documentKey — FE-generated UUID v7 used as the SharePoint document key.
 * @param referenceNumber — claim reference number (string form of the Long).
 * @param requiredDocuments — typed list of `DocumentTypeRef` the proof may
 *        classify as. Sent as a comma-separated list of subtype api-values on
 *        the query string (e.g. `"MedicalReport,PaymentProof,TravelProof"`).
 *        The BFF derives the parent category from the subtype catalog. The
 *        JSON-array-in-URL form was abandoned — Tomcat rejects `[`/`]` per
 *        RFC 3986. For medical claims, Docmana uses this as classification
 *        context; for non-medical claims it's stored as the upload subtype on
 *        the AMQP message to SharePoint.
 * @param clientMessage — optional free-text from the claimant (≤ 5000 chars),
 *        forwarded to NOVA as the Interaction Details description.
 */
export async function uploadProof(
  file: File,
  eventNumber: string,
  claimType: string,
  documentKey: string,
  referenceNumber: string,
  requiredDocuments: DocumentTypeRef[],
  clientMessage?: string
): Promise<ProofUploadResponse> {
  const formData = new FormData();
  appendSafeFile(formData, 'file', file);
  // clientMessage rides in the multipart body, not the query-string: free text with French
  // apostrophes/newlines trips the Azure WAF SQLI/CRLF rules when URL-encoded (DEV-7030).
  // Spring's @RequestParam resolves it from the form-field, so the BFF contract is unchanged.
  if (clientMessage) {
    formData.append('clientMessage', clientMessage);
  }

  const response = await axiosInstance.post<SuccessResponse<ProofUploadResponse>>(
    '/api/v1/documents/uploadProof',
    formData,
    {
      params: {
        eventNumber,
        claimType,
        documentKey,
        referenceNumber,
        requiredDocuments: requiredDocuments.join(','),
      },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 360000,
      suppressGlobalErrorToast: true,
    }
  );

  return response.data.payload;
}

/**
 * Upload void check for banking info extraction.
 *
 * POST /api/v1/documents/uploadVoidCheck
 */
export async function uploadVoidCheck(
  file: File,
  eventNumber: string,
  claimType: string,
  documentKey: string,
  referenceNumber: string
): Promise<VoidCheckUploadResponse> {
  const formData = new FormData();
  appendSafeFile(formData, 'file', file);

  const response = await axiosInstance.post<SuccessResponse<VoidCheckUploadResponse>>(
    '/api/v1/documents/uploadVoidCheck',
    formData,
    {
      params: { eventNumber, claimType, documentKey, referenceNumber },
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 360000,
      suppressGlobalErrorToast: true,
    }
  );

  return response.data.payload;
}

/**
 * Determine required documents for a claim. One entry per distinct
 * {@link DocumentTypeRef} the BFF declares required. The FE expands those
 * into the granular UI tiles per claim type via
 * {@code documentRequirementTiles.getTilesForClaim}.
 *
 * POST /api/v1/documents/required-proofs
 */
export async function getRequiredDocuments(
  request: RequiredDocumentsRequest
): Promise<RequiredDocumentsResponse> {
  const response = await axiosInstance.post<RequiredDocumentsResponse>(
    '/api/v1/documents/required-proofs',
    request,
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    }
  );

  // Returns data directly (not wrapped in SuccessResponse).
  return response.data;
}

/**
 * Merge multiple image files into a single PDF document.
 *
 * POST /api/v1/documents/merge — kept on v1 because the merge endpoint
 * carries no document-type semantics (it's a PDF utility, not part of
 * DEV-6164's category/subtype refactor).
 */
export async function mergeDocumentPages(files: File[]): Promise<Blob> {
  if (!files || files.length === 0) {
    throw new Error('mergeDocumentPages requires at least one file');
  }
  const formData = new FormData();
  for (const file of files) {
    appendSafeFile(formData, 'files', file);
  }
  const response = await axiosInstance.post<Blob>(
    '/api/v1/documents/merge',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
      timeout: 60000, // 60s — image-to-PDF conversion
      suppressGlobalErrorToast: true,
    }
  );
  return response.data;
}

/**
 * Download a previously uploaded document by its key.
 *
 * GET /api/v1/documents/{documentKey}/download
 */
export async function downloadDocument(documentKey: string): Promise<Blob> {
  const response = await axiosInstance.get<Blob>(
    `/api/v1/documents/${encodeURIComponent(documentKey)}/download`,
    {
      responseType: 'blob',
      timeout: 10000,
    }
  );
  return response.data;
}
